import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Sparkles, AlertTriangle, ChevronDown, Info, HeartPulse } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { logError } from '../lib/errorLogger';
import MetricPanel from './MetricPanel';
import MethodologyDialog from './MethodologyDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DetoxScoreCircle = ({ score, label, color, onClick, tappable, testId }) => {
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  return (
    <div className={`text-center ${tappable ? 'cursor-pointer' : ''}`} onClick={onClick} data-testid={testId}>
      <div className="relative w-24 h-24 mx-auto">
        <svg className="w-24 h-24 -rotate-90">
          <circle cx="48" cy="48" r="40" stroke="#E5D0C9" strokeWidth="8" fill="none" />
          <circle cx="48" cy="48" r="40" stroke={color} strokeWidth="8" fill="none"
            strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            strokeLinecap="round" className="transition-all duration-700" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-heading text-2xl font-bold text-[#1A1514]">{score}</span>
        </div>
      </div>
      <p className="text-sm text-[#8C7A76] mt-2">{label}</p>
      {tappable && <p className="text-[10px] text-[#4ADE80] mt-0.5">Tap for details</p>}
    </div>
  );
};

const DetoxScoreCard = ({ detoxScore, trackCycle }) => {
  const [open, setOpen] = useState(null); // 'inflammation' | 'gut' | null
  const [data, setData] = useState({});
  const [hormone, setHormone] = useState(null);
  const [showHormone, setShowHormone] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);

  const fetchMetric = useCallback(async (key, url) => {
    try {
      const res = await axios.get(`${API}${url}`, { withCredentials: true });
      setData((prev) => ({ ...prev, [key]: res.data }));
    } catch (err) {
      logError('DetoxScoreCard', `Failed to fetch ${key} analysis`, { error: err?.message });
    }
  }, []);

  useEffect(() => {
    if (open === 'inflammation' && !data.inflammation) fetchMetric('inflammation', '/inflammation/analysis');
    if (open === 'gut' && !data.gut) fetchMetric('gut', '/gut/analysis');
  }, [open, data, fetchMetric]);

  useEffect(() => {
    if (trackCycle) {
      axios.get(`${API}/hormone/analysis`, { withCredentials: true })
        .then((r) => setHormone(r.data))
        .catch((err) => logError('DetoxScoreCard', 'Failed to fetch hormone analysis', { error: err?.message }));
    }
  }, [trackCycle]);

  const toggle = (key) => setOpen((prev) => (prev === key ? null : key));

  return (
    <Card className="lg:col-span-4 card-elevated border-0" data-testid="detox-score-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-[#1A1514]">
            <Sparkles className="w-5 h-5 text-[#4ADE80]" />
            Daily Detox Score
          </CardTitle>
          <button onClick={() => setShowMethodology(true)} data-testid="methodology-btn"
            className="text-[#8C7A76] hover:text-[#FF6B4A] transition-colors flex items-center gap-1 text-xs">
            <Info className="w-4 h-4" /> <span className="hidden sm:inline">How it's scored</span>
          </button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center py-4">
          <div className="score-circle mb-4">{detoxScore?.total_score || 0}</div>
          <p className="text-[#8C7A76] text-sm">out of 100</p>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <DetoxScoreCircle score={detoxScore?.hydration_score || 0} label="Hydration" color="#FF6B4A" testId="ring-hydration" />
          <DetoxScoreCircle score={detoxScore?.gut_health_score || 0} label="Gut Health" color="#4ADE80"
            onClick={() => toggle('gut')} tappable testId="ring-gut" />
          <DetoxScoreCircle score={detoxScore?.inflammation_score || 0} label="Inflammation" color="#A388EE"
            onClick={() => toggle('inflammation')} tappable testId="ring-inflammation" />
        </div>

        {open === 'inflammation' && <MetricPanel analysis={data.inflammation} onClose={() => setOpen(null)} testId="inflammation-panel" />}
        {open === 'gut' && <MetricPanel analysis={data.gut} onClose={() => setOpen(null)} testId="gut-panel" />}

        {/* High inflammation quick alert */}
        {open !== 'inflammation' && detoxScore?.inflammation_score !== undefined && detoxScore.inflammation_score < 50 && (
          <Button variant="ghost" className="w-full mt-3 text-xs text-[#A388EE] hover:bg-[#A388EE]/10 rounded-lg"
            onClick={() => setOpen('inflammation')} data-testid="inflammation-alert-btn">
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
            High inflammation detected — tap to see what's causing it
            <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        )}

        {/* Hormonal Balance (cycle trackers only) */}
        {trackCycle && hormone && hormone.phase !== 'unknown' && (
          <div className="mt-4 border-t border-[#F3E6E0] pt-4">
            <button onClick={() => setShowHormone((p) => !p)}
              className="w-full flex items-center justify-between" data-testid="hormone-toggle">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-full bg-[#A388EE]/15 flex items-center justify-center">
                  <HeartPulse className="w-4 h-4 text-[#A388EE]" />
                </span>
                <div className="text-left">
                  <p className="text-sm font-semibold text-[#1A1514]">Hormonal Balance</p>
                  <p className="text-[11px] text-[#8C7A76]">{hormone.phase_label} · {hormone.level_label}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-heading text-xl font-bold text-[#A388EE]" data-testid="hormone-score">{hormone.score}</span>
                <ChevronDown className={`w-4 h-4 text-[#8C7A76] transition-transform ${showHormone ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {hormone.prediction && !showHormone && (
              <div className="mt-2 bg-[#A388EE]/12 border border-[#A388EE]/30 rounded-lg p-2.5 flex items-start gap-2" data-testid="hormone-prediction-preview">
                <AlertTriangle className="w-3.5 h-3.5 text-[#A388EE] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#1A1514] leading-relaxed">{hormone.prediction.message}</p>
              </div>
            )}
            {showHormone && <MetricPanel analysis={hormone} onClose={() => setShowHormone(false)} testId="hormone-panel" />}
          </div>
        )}
      </CardContent>

      <MethodologyDialog open={showMethodology} onOpenChange={setShowMethodology} />
    </Card>
  );
};

export default DetoxScoreCard;
